import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { parseNaturalQuery, queryToChips } from '../../utils/naturalLanguageParser';

/**
 * Simple debounce helper
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * @param {Array<{id: string, label: string, kind: string}>} value - Current chips
 * @param {Function} onChange - Callback when chips change
 * @param {string} placeholder - Input placeholder text
 * @param {boolean} autoFocus - Auto-focus the input on mount
 */
const COLOR_WORDS = [
  'black', 'white', 'red', 'green', 'blue', 'yellow', 'magenta', 'cyan',
  'gray', 'grey', 'maroon', 'navy', 'purple', 'olive', 'orange', 'pink',
  'brown', 'silver', 'sky blue', 'steel blue', 'light blue', 'dark blue',
  'light green', 'dark green', 'light gray', 'dark gray'
];

/**
 * Complete color mapping from colorMap.ts to accurate hex values
 * All colors referenced in the system with their exact CSS values
 */
const COLOR_TO_CSS = {
  // ═══════════════════════════════════════════════════════════
  // PRIMARY FAMILY COLORS
  // ═══════════════════════════════════════════════════════════
  red: '#E53935',
  blue: '#1E88E5',
  green: '#43A047',
  yellow: '#FDD835',
  orange: '#FB8C00',
  purple: '#8E24AA',
  pink: '#EC407A',
  brown: '#795548',
  black: '#212121',
  white: '#FAFAFA',
  gray: '#9E9E9E',
  grey: '#9E9E9E',
  
  // ═══════════════════════════════════════════════════════════
  // RED FAMILY (burgundy, maroon, rust, crimson, wine, scarlet, cherry, ruby, vermilion, carmine)
  // ═══════════════════════════════════════════════════════════
  burgundy: '#800020',
  maroon: '#800000',
  rust: '#B7410E',
  crimson: '#DC143C',
  wine: '#722F37',
  scarlet: '#FF2400',
  cherry: '#DE3163',
  ruby: '#E0115F',
  vermilion: '#E34234',
  carmine: '#960018',
  
  // ═══════════════════════════════════════════════════════════
  // BLUE FAMILY (navy, azure, cobalt, sky, denim, sapphire, indigo, cerulean, teal, turquoise, aqua)
  // ═══════════════════════════════════════════════════════════
  navy: '#000080',
  azure: '#007FFF',
  cobalt: '#0047AB',
  sky: '#87CEEB',
  denim: '#1560BD',
  sapphire: '#0F52BA',
  indigo: '#4B0082',
  cerulean: '#007BA7',
  teal: '#008080',
  turquoise: '#40E0D0',
  aqua: '#00FFFF',
  cyan: '#00FFFF',
  
  // ═══════════════════════════════════════════════════════════
  // GREEN FAMILY (lime, olive, forest, emerald, mint, sage, jade, chartreuse, hunter, moss)
  // ═══════════════════════════════════════════════════════════
  lime: '#32CD32',
  olive: '#808000',
  forest: '#228B22',
  emerald: '#50C878',
  mint: '#98FB98',
  sage: '#BCB88A',
  jade: '#00A86B',
  chartreuse: '#7FFF00',
  hunter: '#355E3B',
  moss: '#8A9A5B',
  
  // ═══════════════════════════════════════════════════════════
  // BROWN FAMILY (beige, tan, cream, khaki, sand, camel, chocolate, coffee, mocha, taupe, sepia)
  // ═══════════════════════════════════════════════════════════
  beige: '#F5F5DC',
  tan: '#D2B48C',
  cream: '#FFFDD0',
  khaki: '#C3B091',
  sand: '#C2B280',
  camel: '#C19A6B',
  chocolate: '#7B3F00',
  coffee: '#6F4E37',
  mocha: '#967969',
  taupe: '#483C32',
  sepia: '#704214',
  
  // ═══════════════════════════════════════════════════════════
  // YELLOW FAMILY (gold, golden, mustard, lemon, canary, amber, honey, saffron, blonde)
  // ═══════════════════════════════════════════════════════════
  gold: '#FFD700',
  golden: '#FFD700',
  mustard: '#FFDB58',
  lemon: '#FFF44F',
  canary: '#FFEF00',
  amber: '#FFBF00',
  honey: '#EB9605',
  saffron: '#F4C430',
  blonde: '#FAF0BE',
  
  // ═══════════════════════════════════════════════════════════
  // ORANGE FAMILY (tangerine, peach, coral, apricot, pumpkin, copper, terracotta)
  // ═══════════════════════════════════════════════════════════
  tangerine: '#FF9966',
  peach: '#FFCBA4',
  coral: '#FF7F50',
  apricot: '#FBCEB1',
  pumpkin: '#FF7518',
  copper: '#B87333',
  terracotta: '#E2725B',
  
  // ═══════════════════════════════════════════════════════════
  // PURPLE FAMILY (violet, lavender, plum, grape, magenta, mauve, lilac, amethyst, eggplant)
  // ═══════════════════════════════════════════════════════════
  violet: '#EE82EE',
  lavender: '#E6E6FA',
  plum: '#DDA0DD',
  grape: '#6F2DA8',
  magenta: '#FF00FF',
  mauve: '#E0B0FF',
  lilac: '#C8A2C8',
  amethyst: '#9966CC',
  eggplant: '#614051',
  
  // ═══════════════════════════════════════════════════════════
  // PINK FAMILY (rose, blush, salmon, fuchsia, hot pink, bubblegum, carnation)
  // ═══════════════════════════════════════════════════════════
  rose: '#FF007F',
  blush: '#DE5D83',
  salmon: '#FA8072',
  fuchsia: '#FF00FF',
  'hot pink': '#FF69B4',
  hotpink: '#FF69B4',
  bubblegum: '#FFC1CC',
  carnation: '#FFA6C9',
  
  // ═══════════════════════════════════════════════════════════
  // BLACK FAMILY (ebony, charcoal, onyx, jet, obsidian, noir, raven)
  // ═══════════════════════════════════════════════════════════
  ebony: '#555D50',
  charcoal: '#36454F',
  onyx: '#353839',
  jet: '#343434',
  obsidian: '#3D3D3D',
  noir: '#1A1A1A',
  raven: '#0A0A0A',
  
  // ═══════════════════════════════════════════════════════════
  // WHITE FAMILY (ivory, pearl, snow, cream, off-white, eggshell, alabaster)
  // ═══════════════════════════════════════════════════════════
  ivory: '#FFFFF0',
  pearl: '#FDEEF4',
  snow: '#FFFAFA',
  'off-white': '#FAF9F6',
  'off white': '#FAF9F6',
  offwhite: '#FAF9F6',
  eggshell: '#F0EAD6',
  alabaster: '#EDEAE0',
  
  // ═══════════════════════════════════════════════════════════
  // GRAY FAMILY (grey, silver, slate, ash, charcoal, pewter, stone, graphite)
  // ═══════════════════════════════════════════════════════════
  silver: '#C0C0C0',
  slate: '#708090',
  ash: '#B2BEB5',
  pewter: '#8A9A9A',
  stone: '#928E85',
  graphite: '#474954',
};

// Get CSS color for a color name
function getColorCSS(colorName) {
  const lower = colorName.toLowerCase().trim();
  // Try exact match first
  if (COLOR_TO_CSS[lower]) {
    return COLOR_TO_CSS[lower];
  }
  // Try without spaces/hyphens
  const normalized = lower.replace(/[\s-]/g, '');
  if (COLOR_TO_CSS[normalized]) {
    return COLOR_TO_CSS[normalized];
  }
  // Fall back to the name itself (works for CSS named colors)
  return lower;
}

const TYPE_WORDS = ['notes', 'images', 'pdfs', 'links', 'videos'];

function classifyToken(raw) {
  const t = raw.trim().toLowerCase();
  
  // Negation: starts with - or "less "
  if (t.startsWith('-') || t.startsWith('less ')) {
    return 'not';
  }
  
  // Color detection
  if (COLOR_WORDS.some(color => t === color || t.includes(color))) {
    return 'color';
  }
  
  // Type detection
  if (TYPE_WORDS.includes(t)) {
    return 'type';
  }
  
  // Time detection
  const timePatterns = ['today', 'yesterday', 'last week', 'last month', 'this year'];
  if (timePatterns.includes(t) || /\d{4}-\d{2}/.test(t)) {
    return 'time';
  }
  
  return 'term';
}

function getColorTint(kind, label) {
  const lower = label.toLowerCase();
  
  if (kind === 'color') {
    // Left border accent style - very light background with strong left border
    const colorMap = {
      'blue': 'bg-blue-50 text-blue-900 border-l-4 border-blue-500',
      'green': 'bg-emerald-50 text-emerald-900 border-l-4 border-emerald-500',
      'red': 'bg-red-50 text-red-900 border-l-4 border-red-500',
      'yellow': 'bg-yellow-50 text-yellow-900 border-l-4 border-yellow-500',
      'purple': 'bg-purple-50 text-purple-900 border-l-4 border-purple-500',
      'orange': 'bg-orange-50 text-orange-900 border-l-4 border-orange-500',
      'pink': 'bg-pink-50 text-pink-900 border-l-4 border-pink-500',
      'black': 'bg-gray-50 text-gray-900 border-l-4 border-gray-700',
      'white': 'bg-gray-50 text-gray-900 border-l-4 border-gray-300',
      'brown': 'bg-amber-50 text-amber-900 border-l-4 border-amber-600',
      'gray': 'bg-gray-50 text-gray-900 border-l-4 border-gray-500',
      'grey': 'bg-gray-50 text-gray-900 border-l-4 border-gray-500',
    };
    
    for (const [color, classes] of Object.entries(colorMap)) {
      if (lower.includes(color)) {
        return classes;
      }
    }
    return 'bg-purple-50 text-purple-900 border-l-4 border-purple-500'; // Default color chip
  }
  
  if (kind === 'type') {
    return 'bg-emerald-100 text-emerald-900';
  }
  
  if (kind === 'time') {
    return 'bg-sky-100 text-sky-900';
  }
  
  if (kind === 'not') {
    return 'bg-rose-100 text-rose-900';
  }
  
  return 'bg-neutral-100 text-neutral-900';
}

export default function ChipInput({
  value = [],
  onChange,
  placeholder = 'Search your Swirlspace…',
  autoFocus = false,
  onSubmit = null,
  hideSuggestions = false,
  compact = false
}) {
  const [input, setInput] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownCloseTimeoutRef = useRef(null);

  // Handle mouse leave on dropdown - close after 2 seconds
  const handleDropdownMouseLeave = useCallback(() => {
    dropdownCloseTimeoutRef.current = setTimeout(() => {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }, 2000);
  }, []);

  // Handle mouse enter on dropdown - cancel close timeout
  const handleDropdownMouseEnter = useCallback(() => {
    if (dropdownCloseTimeoutRef.current) {
      clearTimeout(dropdownCloseTimeoutRef.current);
      dropdownCloseTimeoutRef.current = null;
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownCloseTimeoutRef.current) {
        clearTimeout(dropdownCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const removeChip = useCallback((id) => {
    onChange(value.filter(c => c.id !== id));
  }, [value, onChange]);

  // Listen for chip removal events from OrganizationLayer
  useEffect(() => {
    const handleRemoveChip = (event) => {
      const chipId = event.detail;
      if (chipId) {
        removeChip(chipId);
      }
    };

    window.addEventListener('removeChip', handleRemoveChip);
    return () => {
      window.removeEventListener('removeChip', handleRemoveChip);
    };
  }, [removeChip]);

  // Fetch suggestions from deepsearchService
  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }

      setLoadingSuggestions(true);
      try {
        if (window.deepsearchService && window.deepsearchService.getSuggestions) {
          const result = await window.deepsearchService.getSuggestions(query, { limit: 8 });
          setSuggestions(result.suggestions || []);
          setShowDropdown((result.suggestions || []).length > 0);
        }
      } catch (error) {
        console.error('[ChipInput] Suggestions fetch failed:', error);
        setSuggestions([]);
        setShowDropdown(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300),
    []
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && 
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addChip = useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    
    // Try natural language parsing first
    const { filters, remainingText } = parseNaturalQuery(trimmed);
    
    if (filters.length > 0) {
      // Natural language query detected - add all parsed filters
      setIsParsing(true);
      const newChips = [...value, ...filters];
      
      // Add remaining text as term if meaningful
      if (remainingText && remainingText.length > 2) {
        newChips.push({
          id: crypto.randomUUID(),
          label: remainingText,
          kind: classifyToken(remainingText),
        });
      }
      
      onChange(newChips);
      setInput('');
      setTimeout(() => setIsParsing(false), 300);
    } else {
      // Fallback to simple token classification
      const newChip = {
        id: crypto.randomUUID(),
        label: trimmed,
        kind: classifyToken(trimmed)
      };
      
      onChange([...value, newChip]);
      setInput('');
    }
  }, [value, onChange]);

  // Handle selecting a suggestion
  const handleSelectSuggestion = useCallback((suggestion) => {
    const newChip = {
      id: crypto.randomUUID(),
      label: suggestion.value,
      kind: suggestion.type
    };
    
    onChange([...value, newChip]);
    setInput('');
    setShowDropdown(false);
    setSelectedIndex(-1);
    setSuggestions([]);
  }, [value, onChange]);

  const handleKeyDown = (e) => {
    // Handle dropdown navigation
    if (showDropdown && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          return;
        
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          return;
        
        case 'Escape':
          e.preventDefault();
          setShowDropdown(false);
          setSelectedIndex(-1);
          return;
        
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            e.preventDefault();
            handleSelectSuggestion(suggestions[selectedIndex]);
            return;
          }
          break;
      }
    }
    
    // Original key handling
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      addChip(input);
      setShowDropdown(false);
      // Call onSubmit callback if provided (e.g., for floating bar to scroll to main)
      if (onSubmit) {
        setTimeout(() => onSubmit(), 50);
      }
    } else if (e.key === 'Enter' && !input.trim() && value.length > 0) {
      // Enter with no input but has chips - trigger submit
      e.preventDefault();
      if (onSubmit) {
        onSubmit();
      }
    } else if (e.key === 'Backspace' && !input && value.length > 0) {
      removeChip(value[value.length - 1].id);
    }
  };

  return (
    <div className="relative w-full">
      <div className={compact 
        ? "w-full bg-transparent" 
        : "w-full rounded-xl border border-neutral-100/80 bg-white px-4 py-2 shadow-md shadow-neutral-100/80 focus-within:shadow-lg focus-within:shadow-neutral-200/50 focus-within:border-neutral-200/60 transition-all duration-300 ease-out"
      }>
      <div className={`flex flex-wrap items-center ${compact ? 'gap-1.5' : 'gap-2'}`}>
        <AnimatePresence>
          {/* Sort chips: color chips first, then others */}
          {[...value].sort((a, b) => {
            if (a.kind === 'color' && b.kind !== 'color') return -1;
            if (a.kind !== 'color' && b.kind === 'color') return 1;
            return 0;
          }).map((chip) => (
            <motion.span
              key={chip.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`inline-flex items-center rounded-full font-medium shadow-sm transition-colors group relative ${getColorTint(chip.kind, chip.label)} ${
                compact ? 'gap-1.5 px-2.5 py-1 text-sm' : 'gap-1.5 px-3 py-1 text-sm'
              }`}
              title={chip.kind === 'color' ? `Color filter: ${chip.label}` : undefined}
            >
              {chip.label}
              <button
                onClick={() => removeChip(chip.id)}
                className="opacity-70 hover:opacity-100 transition-opacity"
                aria-label={`Remove ${chip.label}`}
              >
                <X className="h-3 w-3" />
              </button>
              {/* Tooltip for color chips */}
              {chip.kind === 'color' && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                  Color filter: {chip.label}
                </span>
              )}
            </motion.span>
          ))}
        </AnimatePresence>
        
        <div className={`flex-1 relative ${compact ? 'min-w-[100px]' : 'min-w-[160px]'}`}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              const value = e.target.value;
              setInput(value);
              
              // Trigger suggestions if input is >= 2 chars (unless disabled)
              if (!hideSuggestions && value.trim().length >= 2) {
                fetchSuggestions(value.trim());
                setSelectedIndex(-1);
              } else {
                setShowDropdown(false);
                setSuggestions([]);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            className={`w-full outline-none bg-transparent text-neutral-700 placeholder:text-neutral-400 ${
              compact 
                ? 'py-0.5 text-base placeholder:font-normal' 
                : 'py-1 text-base placeholder:font-light'
            }`}
          />
          {isParsing && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#7A47F5]"></div>
            </div>
          )}
        </div>
      </div>
      
      {/* Show interpreted query context when natural language is detected */}
      {input && input.length > 10 && (
        <div className="mt-2 text-xs text-neutral-500 italic">
          Try: "instagram videos from last week" or "images tagged workspace"
        </div>
      )}
      </div>

      {/* Typeahead Suggestions Dropdown */}
      {!hideSuggestions && showDropdown && suggestions.length > 0 && (
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-neutral-200 overflow-hidden z-[100]"
          style={{ maxHeight: '280px', overflowY: 'auto' }}
          onMouseLeave={handleDropdownMouseLeave}
          onMouseEnter={handleDropdownMouseEnter}
        >
          {suggestions.map((suggestion, index) => {
            const isSelected = selectedIndex === index;
            return (
              <button
                key={`${suggestion.type}-${suggestion.value}`}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`
                  w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors border-none cursor-pointer
                  ${isSelected 
                    ? 'bg-purple-50 text-purple-900' 
                    : 'bg-white hover:bg-neutral-50 text-neutral-800'
                  }
                `}
              >
                {/* Icon based on type */}
                {suggestion.type === 'color' && (
                  <div 
                    className="w-4 h-4 rounded-full border border-neutral-200 flex-shrink-0 shadow-sm" 
                    style={{
                      background: getColorCSS(suggestion.value)
                    }} 
                  />
                )}
                {suggestion.type === 'tag' && (
                  <span className="text-neutral-400 text-sm flex-shrink-0">#</span>
                )}
                
                {/* Suggestion text */}
                <span className="flex-1 text-sm">
                  {suggestion.value}
                </span>
                
                {/* Type badge */}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  suggestion.type === 'color' ? 'bg-purple-100 text-purple-700' :
                  suggestion.type === 'tag' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-neutral-100 text-neutral-600'
                }`}>
                  {suggestion.type}
                </span>
                
                {/* Count badge (if available) */}
                {suggestion.count && (
                  <span className="text-xs text-neutral-400 flex-shrink-0">
                    {suggestion.count}
                  </span>
                )}
              </button>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

